import { useCallback, useEffect, useMemo, useState } from 'react'
import { normalizeStaffName } from '../lib/csvParser.js'
import { supabase } from '../lib/supabase.js'
import { useCurrentUnit } from './useCurrentUnit.js'

// Drives the Settings screen.
// Fetches the current restaurant + all items + all suppliers (active and
// inactive) so the manager can edit anything without the screen needing
// secondary loads.
//
// Uses the first-active-restaurant lookup pattern (matches Phase 2A
// backward compat). Phase 2B will replace this with an auth-aware
// restaurant_id pulled from the user's session.
export function useSettingsData() {
  const [restaurant, setRestaurant] = useState(null)
  const [items, setItems]           = useState([])
  const [suppliers, setSuppliers]   = useState([])
  const [staff, setStaff]           = useState([])
  // Free-text supplier names found in receive_logs that don't yet have
  // a row in the suppliers catalog. Surfaced in Settings so the manager
  // can promote them in one tap. Each entry: { name, receiving_count }.
  const [unmatchedSuppliers, setUnmatchedSuppliers] = useState([])
  // Daily sales summary — populated via CSV import. Used for the
  // "% of sales" display on the Huddle waste card and the Settings
  // rollup panel (today / 7d / 30d totals + recent imports list).
  const [salesSummary, setSalesSummary] = useState({
    row_count: 0, first_date: null, last_date: null, last_imported_at: null,
    today_net: 0, week_net: 0, month_net: 0,
    today_gross: 0, week_gross: 0, month_gross: 0,
    today_tx: 0, week_tx: 0, month_tx: 0,
    recent_days: [], // last ~7 imported rows for the Settings list
  })
  // Item sales summary — populated by the Item Sales CSV import.
  // Drives the Reports → Variance feature. Tracks how many rows we
  // have, what the latest period covers, and a per-item last-7-days
  // velocity map for the recipe rows hint.
  const [itemSalesSummary, setItemSalesSummary] = useState({
    row_count: 0,
    first_period_start: null,
    last_period_end: null,
    last_imported_at: null,
    matched_count: 0,
    unmatched_count: 0,
    last7d_units_by_item_id: {},   // { item_id: total quantity_sold last 7 days }
  })
  // Latest baseline snapshot (most recent on_hand_snapshots row with
  // source='baseline'). Surfaced in Settings so the manager knows
  // whether they've set their variance starting line.
  const [latestBaseline, setLatestBaseline] = useState(null)
  const [loading, setLoading]       = useState(true)
  const [error, setError]           = useState(null)

  // Multi-unit scope — null for single-unit (resolution byte-identical).
  const { currentRestaurantId } = useCurrentUnit()

  const fetchAll = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      // 1. The current restaurant. For a grouped operator we resolve the
      //    SELECTED unit; for single-unit (null) we keep the original
      //    first-active lookup byte-for-byte. Everything downstream derives
      //    restId = rest.id, so items/suppliers/staff/sales/baseline follow.
      const { data: rest, error: restErr } = currentRestaurantId
        ? await supabase
            .from('restaurants')
            .select('*')
            .eq('id', currentRestaurantId)
            .single()
        : await supabase
            .from('restaurants')
            .select('*')
            .eq('active', true)
            .order('created_at')
            .limit(1)
            .single()
      if (restErr) throw restErr
      setRestaurant(rest)

      const restId = rest.id

      // 2. Items + suppliers + staff, scoped to that restaurant. Includes
      //    inactive rows so the manager can re-activate things they
      //    previously hid.
      const [itemsRes, supplierRes, staffRes] = await Promise.all([
        supabase
          .from('items')
          .select(`
            id, name, category, unit, cost_per_unit, sale_price, par_qty, current_on_hand,
            last_stocktake_at, lead_time_days, preferred_supplier_id, active,
            pack_size, pack_unit,
            toast_guid, toast_raw_unit, sync_status,
            name_override, cost_override, unit_override
          `)
          .eq('restaurant_id', restId)
          .order('name'),
        supabase
          .from('suppliers')
          .select('id, name, contact, notes, active')
          .eq('restaurant_id', restId)
          .order('name'),
        supabase
          .from('staff')
          .select('id, name, role, hourly_wage, email, phone, notes, active')
          .eq('restaurant_id', restId)
          .order('name'),
      ])
      if (itemsRes.error)    throw itemsRes.error
      if (supplierRes.error) throw supplierRes.error
      if (staffRes.error)    throw staffRes.error

      setItems(itemsRes.data ?? [])
      setSuppliers(supplierRes.data ?? [])
      setStaff(staffRes.data ?? [])

      // Free-text suppliers from receivings — anything where the
      // log has supplier_name but supplier_id is null (manager typed
      // a new name in the Receive form). Group + count so we can
      // show "Mike's Smokehouse · 3 deliveries" in the UI.
      const supplierSet = new Set(
        (supplierRes.data ?? []).map((s) => s.name.toLowerCase())
      )
      const { data: rawReceives } = await supabase
        .from('receive_logs')
        .select('supplier_name')
        .eq('restaurant_id', restId)
        .is('supplier_id', null)
        .not('supplier_name', 'is', null)
      const counts = new Map()
      for (const r of rawReceives ?? []) {
        const name = r.supplier_name?.trim()
        if (!name) continue
        // Don't suggest names that already exist in the catalog
        // (case-insensitive match).
        if (supplierSet.has(name.toLowerCase())) continue
        counts.set(name, (counts.get(name) ?? 0) + 1)
      }
      setUnmatchedSuppliers(
        [...counts.entries()]
          .map(([name, receiving_count]) => ({ name, receiving_count }))
          .sort((a, b) => b.receiving_count - a.receiving_count)
      )

      // Sales summary — pull all imported rows so we can compute
      // today / 7-day / 30-day rollups + a recent list. The query is
      // bounded by the size of the imported window (typically ≤ 365
      // rows for a restaurant with a year of history), so this is
      // cheap. If we ever import multi-year history, switch to a
      // server-side view.
      const { data: salesAgg } = await supabase
        .from('daily_sales')
        .select('sale_date, gross_sales, net_sales, transaction_count, imported_at')
        .eq('restaurant_id', restId)
        .order('sale_date', { ascending: true })

      if (salesAgg && salesAgg.length > 0) {
        // Browser-local YYYY-MM-DD — same convention used in useHuddleData.
        // Phase 2C will pin this to restaurant.timezone explicitly.
        const todayStr = new Date().toLocaleDateString('en-CA')
        const weekAgo = new Date()
        weekAgo.setDate(weekAgo.getDate() - 6) // 7-day window incl. today
        const weekAgoStr = weekAgo.toLocaleDateString('en-CA')
        const monthAgo = new Date()
        monthAgo.setDate(monthAgo.getDate() - 29) // 30-day window incl. today
        const monthAgoStr = monthAgo.toLocaleDateString('en-CA')

        let today_net = 0, week_net = 0, month_net = 0
        let today_gross = 0, week_gross = 0, month_gross = 0
        let today_tx = 0, week_tx = 0, month_tx = 0
        let lastImport = null

        for (const r of salesAgg) {
          const net = Number(r.net_sales || 0)
          const gross = Number(r.gross_sales || 0)
          const tx = Number(r.transaction_count || 0)
          if (r.sale_date === todayStr) {
            today_net   += net
            today_gross += gross
            today_tx    += tx
          }
          if (r.sale_date >= weekAgoStr && r.sale_date <= todayStr) {
            week_net   += net
            week_gross += gross
            week_tx    += tx
          }
          if (r.sale_date >= monthAgoStr && r.sale_date <= todayStr) {
            month_net   += net
            month_gross += gross
            month_tx    += tx
          }
          if (!lastImport || r.imported_at > lastImport) lastImport = r.imported_at
        }

        // Last 7 imported days (most-recent-first) for the Settings list
        const recent = [...salesAgg]
          .sort((a, b) => (a.sale_date < b.sale_date ? 1 : -1))
          .slice(0, 7)

        setSalesSummary({
          row_count: salesAgg.length,
          first_date: salesAgg[0].sale_date,
          last_date:  salesAgg[salesAgg.length - 1].sale_date,
          last_imported_at: lastImport,
          today_net, week_net, month_net,
          today_gross, week_gross, month_gross,
          today_tx, week_tx, month_tx,
          recent_days: recent,
        })
      } else {
        setSalesSummary({
          row_count: 0, first_date: null, last_date: null, last_imported_at: null,
          today_net: 0, week_net: 0, month_net: 0,
          today_gross: 0, week_gross: 0, month_gross: 0,
          today_tx: 0, week_tx: 0, month_tx: 0,
          recent_days: [],
        })
      }

      // ---- Item sales (per-menu-item velocity, for variance) -----
      // Pull the recent window only — last 60 days is plenty for the
      // 7-day-units summary and the row counts on Settings. Variance
      // report itself queries on demand for whatever window the user
      // picks.
      const sixtyDaysAgo = new Date()
      sixtyDaysAgo.setDate(sixtyDaysAgo.getDate() - 60)
      const sixtyAgoStr = sixtyDaysAgo.toLocaleDateString('en-CA')
      const { data: itemSalesAgg } = await supabase
        .from('item_sales')
        .select('item_id, item_name, period_start, period_end, quantity_sold, imported_at')
        .eq('restaurant_id', restId)
        .gte('period_end', sixtyAgoStr)
        .order('period_end', { ascending: false })
        .limit(2000)

      if (itemSalesAgg && itemSalesAgg.length > 0) {
        const todayStr = new Date().toLocaleDateString('en-CA')
        const weekAgo  = new Date()
        weekAgo.setDate(weekAgo.getDate() - 6)
        const weekAgoStr = weekAgo.toLocaleDateString('en-CA')

        const last7d = {}
        let matched = 0, unmatched = 0
        let firstStart = null, lastEnd = null, lastImport = null
        for (const r of itemSalesAgg) {
          if (r.item_id) matched++; else unmatched++
          if (firstStart == null || r.period_start < firstStart) firstStart = r.period_start
          if (lastEnd == null    || r.period_end   > lastEnd)    lastEnd    = r.period_end
          if (!lastImport || r.imported_at > lastImport) lastImport = r.imported_at
          // Last-7-days velocity: include rows whose period overlaps the
          // last-7 window. Approximation: if period_end is within the
          // last 7 days, include the full quantity (Toast exports are
          // usually for narrow windows so this is close enough).
          if (r.item_id && r.period_end >= weekAgoStr && r.period_end <= todayStr) {
            last7d[r.item_id] = (last7d[r.item_id] || 0) + Number(r.quantity_sold || 0)
          }
        }
        setItemSalesSummary({
          row_count: itemSalesAgg.length,
          first_period_start: firstStart,
          last_period_end: lastEnd,
          last_imported_at: lastImport,
          matched_count: matched,
          unmatched_count: unmatched,
          last7d_units_by_item_id: last7d,
        })
      } else {
        setItemSalesSummary({
          row_count: 0, first_period_start: null, last_period_end: null,
          last_imported_at: null, matched_count: 0, unmatched_count: 0,
          last7d_units_by_item_id: {},
        })
      }

      // ---- Latest baseline snapshot ----
      const { data: baselineRow } = await supabase
        .from('on_hand_snapshots')
        .select('snapshot_at, source')
        .eq('restaurant_id', restId)
        .eq('source', 'baseline')
        .order('snapshot_at', { ascending: false })
        .limit(1)
        .maybeSingle()
      setLatestBaseline(baselineRow ?? null)
    } catch (e) {
      setError(e)
      // eslint-disable-next-line no-console
      console.error('[useSettingsData]', e)
    } finally {
      setLoading(false)
    }
  }, [currentRestaurantId])

  useEffect(() => { fetchAll() }, [fetchAll])

  // ---------- Restaurant updates ----------------------------
  // Patches the restaurants row. When daily_target or large_entry_threshold
  // change, also keeps the settings row in sync (the savings function still
  // reads target from settings — Phase 2C will consolidate to one source).
  // After a target change, also kicks off a recompute so today's savings
  // tile reflects the new baseline immediately.
  const updateRestaurant = useCallback(async (patch) => {
    if (!restaurant) return
    const updates = { ...patch, updated_at: new Date().toISOString() }
    const { error: updErr } = await supabase
      .from('restaurants')
      .update(updates)
      .eq('id', restaurant.id)
    if (updErr) throw updErr

    // Mirror target changes into settings (legacy read path).
    const settingsPatch = {}
    if (patch.daily_target != null) settingsPatch.daily_target = patch.daily_target
    if (patch.large_entry_threshold != null) settingsPatch.large_entry_threshold = patch.large_entry_threshold
    if (Object.keys(settingsPatch).length > 0) {
      settingsPatch.updated_at = new Date().toISOString()
      await supabase
        .from('settings')
        .update(settingsPatch)
        .eq('restaurant_id', restaurant.id)

      // Recompute today's savings so the tile updates against the new target.
      const kitchenDate = new Date().toLocaleDateString('en-CA', {
        timeZone: restaurant.timezone || 'America/Chicago',
      })
      await supabase.rpc('recompute_daily_savings', {
        target_date: kitchenDate,
        p_restaurant_id: restaurant.id,
      })
    }

    await fetchAll()
  }, [restaurant, fetchAll])

  // ---------- Item updates ----------------------------------
  // patch is a partial Item. The override flags should be flipped
  // automatically by the calling UI when fields are explicitly edited.
  const updateItem = useCallback(async (itemId, patch) => {
    const { error: updErr } = await supabase
      .from('items')
      .update(patch)
      .eq('id', itemId)
    if (updErr) throw updErr
    await fetchAll()
  }, [fetchAll])

  // Bulk-import items from a POS CSV (Toast Item Sales / Menu Items
  // export, or any other vendor with item-level rows).
  //
  // Match strategy: case-insensitive name match against existing items
  // in this restaurant. Existing matches get a partial update (only
  // fields that are currently null get filled) — never overwrites the
  // manager's edits. Unmatched rows are inserted with sale_price,
  // category, and (if estimateCost=true) a 30%-of-price cost estimate.
  //
  // Returns { inserted, updated, skipped } so the toast can summarize.
  const importItemsFromCsv = useCallback(async (rows, options = {}) => {
    if (!restaurant) throw new Error('No restaurant loaded')
    const { estimateCost = false } = options
    const restId = restaurant.id

    // Map existing items by lowercase name for O(1) matching.
    const existingByName = new Map()
    for (const i of items) {
      existingByName.set((i.name || '').toLowerCase().trim(), i)
    }

    const inserts = []
    const updates = []
    let skipped = 0

    for (const r of rows) {
      const name = (r.name || '').trim()
      if (!name) { skipped++; continue }
      const key = name.toLowerCase()
      const existing = existingByName.get(key)

      if (existing) {
        // Partial update: only fill in null/missing fields.
        const patch = {}
        if ((existing.category == null || existing.category === '') && r.category) {
          patch.category = r.category
        }
        if (existing.sale_price == null && r.sale_price != null) {
          patch.sale_price = r.sale_price
        }
        // If the existing row has $0 cost AND the manager opted into the
        // estimate AND we have a sale price → fill in an estimate.
        if (
          estimateCost &&
          (existing.cost_per_unit == null || Number(existing.cost_per_unit) === 0) &&
          r.sale_price != null
        ) {
          patch.cost_per_unit = Number((r.sale_price * 0.3).toFixed(2))
        }
        if (Object.keys(patch).length > 0) {
          updates.push({ id: existing.id, patch })
        }
      } else {
        const cost = estimateCost && r.sale_price != null
          ? Number((r.sale_price * 0.3).toFixed(2))
          : 0
        inserts.push({
          restaurant_id: restId,
          name,
          category: r.category || null,
          unit: 'each',           // sensible default; manager can refine per item
          cost_per_unit: cost,
          sale_price: r.sale_price ?? null,
          par_qty: 0,
          current_on_hand: 0,
          active: true,
          toast_guid: r.toast_guid || null,
        })
      }
    }

    // Run inserts in a single batch.
    if (inserts.length > 0) {
      const { error: insErr } = await supabase.from('items').insert(inserts)
      if (insErr) throw insErr
    }

    // Run updates one at a time — Supabase doesn't have a bulk
    // PATCH-by-id pattern. The catalog is small (< 200 items) so
    // this is fine.
    for (const u of updates) {
      const { error: updErr } = await supabase
        .from('items')
        .update(u.patch)
        .eq('id', u.id)
      if (updErr) throw updErr
    }

    await fetchAll()
    return {
      inserted: inserts.length,
      updated:  updates.length,
      skipped,
    }
  }, [restaurant, items, fetchAll])

  // Manually create an item (the "+ Add item" button in Settings).
  // Toast-imported items come in via the toast-sync edge function and
  // hit a different path — this is purely for managers adding kitchen
  // items that the POS doesn't track (prep ingredients, dry goods, etc.).
  const createItem = useCallback(async (payload) => {
    if (!restaurant) throw new Error('No restaurant loaded')
    const { data, error: insErr } = await supabase
      .from('items')
      .insert({
        restaurant_id: restaurant.id,
        name:           payload.name?.trim(),
        category:       payload.category?.trim() || null,
        unit:           payload.unit || 'each',
        cost_per_unit:  Number(payload.cost_per_unit) || 0,
        sale_price:     payload.sale_price != null ? Number(payload.sale_price) : null,
        par_qty:        Number(payload.par_qty) || 0,
        current_on_hand: Number(payload.current_on_hand) || 0,
        preferred_supplier_id: payload.preferred_supplier_id || null,
        // Pack-aware fields (default 1 / null = "no pack relationship",
        // matching the migration default and existing behavior).
        pack_size:      payload.pack_size != null ? Number(payload.pack_size) || 1 : 1,
        pack_unit:      payload.pack_unit?.trim() || null,
        active:         true,
      })
      .select()
      .single()
    if (insErr) throw insErr
    await fetchAll()
    return data
  }, [restaurant, fetchAll])

  // ---------- Supplier updates / creates --------------------
  const updateSupplier = useCallback(async (supplierId, patch) => {
    const { error: updErr } = await supabase
      .from('suppliers')
      .update(patch)
      .eq('id', supplierId)
    if (updErr) throw updErr
    await fetchAll()
  }, [fetchAll])

  const createSupplier = useCallback(async ({ name, contact, notes }) => {
    if (!restaurant) throw new Error('No restaurant loaded')
    const { data, error: insErr } = await supabase
      .from('suppliers')
      .insert({
        restaurant_id: restaurant.id,
        name: name.trim(),
        contact: contact?.trim() || null,
        notes: notes?.trim() || null,
      })
      .select()
      .single()
    if (insErr) throw insErr
    await fetchAll()
    return data
  }, [restaurant, fetchAll])

  // Promote a free-text supplier name from receive_logs into the
  // catalog. Creates the supplier row AND backfills supplier_id on
  // existing receive_logs rows that match by name (case-insensitive),
  // so the unmatched list cleans itself up.
  const promoteSupplier = useCallback(async (name) => {
    if (!restaurant) throw new Error('No restaurant loaded')
    const trimmedName = name.trim()
    if (!trimmedName) throw new Error('name is empty')

    // 1. Create the supplier
    const { data: newSupplier, error: insErr } = await supabase
      .from('suppliers')
      .insert({ restaurant_id: restaurant.id, name: trimmedName })
      .select()
      .single()
    if (insErr) throw insErr

    // 2. Backfill existing receive_logs that used this name as free-text.
    //    ilike = case-insensitive match so 'mike\'s' = 'Mike\'s'.
    const { error: updErr } = await supabase
      .from('receive_logs')
      .update({ supplier_id: newSupplier.id })
      .eq('restaurant_id', restaurant.id)
      .ilike('supplier_name', trimmedName)
      .is('supplier_id', null)
    if (updErr) throw updErr

    await fetchAll()
    return newSupplier
  }, [restaurant, fetchAll])

  // Active items + inactive items, kept separate so the UI can collapse
  // the inactive group by default.
  const activeItems = useMemo(() => items.filter((i) => i.active), [items])
  const inactiveItems = useMemo(() => items.filter((i) => !i.active), [items])

  // ---------- Staff CRUD -----------------------------------
  const updateStaff = useCallback(async (staffId, patch) => {
    const updates = { ...patch, updated_at: new Date().toISOString() }
    const { error: updErr } = await supabase
      .from('staff')
      .update(updates)
      .eq('id', staffId)
    if (updErr) throw updErr
    await fetchAll()
  }, [fetchAll])

  // ---------- Sales delete ---------------------------------
  // Remove a single imported day. Used by the × button next to each
  // row in the "Most recent" list.
  const deleteDailySales = useCallback(async (saleDate) => {
    if (!restaurant) throw new Error('No restaurant loaded')
    const { error: delErr } = await supabase
      .from('daily_sales')
      .delete()
      .eq('restaurant_id', restaurant.id)
      .eq('sale_date', saleDate)
    if (delErr) throw delErr
    await fetchAll()
  }, [restaurant, fetchAll])

  // Wipe all imported sales for this restaurant. Used when the manager
  // wants to start over (e.g., uploaded the wrong file, switching POS).
  // Confirmation lives in the UI — this just executes.
  const deleteAllDailySales = useCallback(async () => {
    if (!restaurant) throw new Error('No restaurant loaded')
    const { error: delErr } = await supabase
      .from('daily_sales')
      .delete()
      .eq('restaurant_id', restaurant.id)
    if (delErr) throw delErr
    await fetchAll()
  }, [restaurant, fetchAll])

  // ---------- Sales CSV import -----------------------------
  // Bulk upsert daily_sales rows. Conflict on (restaurant_id, sale_date)
  // so re-uploading the same range overwrites cleanly. Returns the
  // count of inserted/updated rows for the toast.
  const importDailySales = useCallback(async (rows) => {
    if (!restaurant) throw new Error('No restaurant loaded')
    if (!Array.isArray(rows) || rows.length === 0) return 0
    const payload = rows.map((r) => ({
      restaurant_id:     restaurant.id,
      sale_date:         r.sale_date,
      net_sales:         r.net_sales,
      gross_sales:       r.gross_sales,
      transaction_count: r.transaction_count,
      source:            'csv',
      imported_at:       new Date().toISOString(),
    }))
    const { error: upErr } = await supabase
      .from('daily_sales')
      .upsert(payload, { onConflict: 'restaurant_id,sale_date' })
    if (upErr) throw upErr
    await fetchAll()
    return rows.length
  }, [restaurant, fetchAll])

  // Bulk import staff from a CSV roster. Same case-insensitive name
  // matching as the items import: existing rows get partial updates
  // (only fields that are currently null/empty get filled — never
  // overwrites manager edits). New rows are inserted with active=true.
  // Returns { inserted, updated, skipped } so the toast can summarize.
  const importStaffFromCsv = useCallback(async (rows) => {
    if (!restaurant) throw new Error('No restaurant loaded')
    const restId = restaurant.id

    const existingByName = new Map()
    for (const s of staff) {
      existingByName.set(normalizeStaffName(s.name), s)
    }

    const inserts = []
    const updates = []
    let skipped = 0

    for (const r of rows) {
      const name = (r.name || '').trim()
      if (!name) { skipped++; continue }
      // Defense-in-depth: even though parseStaffCsv already enforces
      // first+last, the mutation can be called from elsewhere later.
      // Single-token names get skipped here too rather than risk
      // ambiguous matching against existing multi-word staff.
      if (name.split(/\s+/).filter(Boolean).length < 2) { skipped++; continue }
      const key = normalizeStaffName(name)
      const existing = existingByName.get(key)

      if (existing) {
        const patch = {}
        if ((existing.role == null || existing.role === '') && r.role) patch.role = r.role
        if (existing.hourly_wage == null && r.hourly_wage != null) patch.hourly_wage = r.hourly_wage
        if ((existing.email == null || existing.email === '') && r.email) patch.email = r.email
        if ((existing.phone == null || existing.phone === '') && r.phone) patch.phone = r.phone
        if ((existing.notes == null || existing.notes === '') && r.notes) patch.notes = r.notes
        if (Object.keys(patch).length > 0) {
          updates.push({ id: existing.id, patch })
        }
      } else {
        inserts.push({
          restaurant_id: restId,
          name,
          role:         r.role || null,
          hourly_wage:  r.hourly_wage,
          email:        r.email || null,
          phone:        r.phone || null,
          notes:        r.notes || null,
          active:       true,
        })
      }
    }

    if (inserts.length > 0) {
      const { error: insErr } = await supabase.from('staff').insert(inserts)
      if (insErr) throw insErr
    }
    for (const u of updates) {
      const { error: updErr } = await supabase
        .from('staff')
        .update({ ...u.patch, updated_at: new Date().toISOString() })
        .eq('id', u.id)
      if (updErr) throw updErr
    }

    await fetchAll()
    return {
      inserted: inserts.length,
      updated:  updates.length,
      skipped,
    }
  }, [restaurant, staff, fetchAll])

  const createStaff = useCallback(async ({ name, role, hourly_wage, email, phone, notes }) => {
    if (!restaurant) throw new Error('No restaurant loaded')
    const { data, error: insErr } = await supabase
      .from('staff')
      .insert({
        restaurant_id: restaurant.id,
        name: name.trim(),
        role: role?.trim() || null,
        hourly_wage: hourly_wage != null && hourly_wage !== '' ? Number(hourly_wage) : null,
        email: email?.trim() || null,
        phone: phone?.trim() || null,
        notes: notes?.trim() || null,
      })
      .select()
      .single()
    if (insErr) throw insErr
    await fetchAll()
    return data
  }, [restaurant, fetchAll])

  // ---------- Item sales import ----------------------------------
  // Saves a Toast Item Sales CSV import to the item_sales table.
  // Each row gets matched to an existing item by toast_guid (exact)
  // or by case-insensitive name (fallback). The matched item_id is
  // saved so the variance report can join cleanly. Re-importing the
  // same period (same restaurant + item + period_start + period_end)
  // overwrites the previous quantities so refining and re-uploading
  // is safe.
  //
  // Payload: { rows, period_start, period_end }
  // Returns: { inserted, updated, unmatched }
  const importItemSales = useCallback(async ({ rows, period_start, period_end }) => {
    if (!restaurant) throw new Error('No restaurant loaded')
    if (!Array.isArray(rows) || rows.length === 0) {
      return { inserted: 0, updated: 0, unmatched: 0 }
    }
    if (!period_start || !period_end) {
      throw new Error('period_start and period_end are required')
    }
    const restId = restaurant.id

    // Match candidates: by guid first, fall back to name.
    const byGuid = new Map()
    const byName = new Map()
    for (const i of items) {
      if (i.toast_guid) byGuid.set(i.toast_guid, i)
      byName.set((i.name || '').toLowerCase().trim(), i)
    }

    // Pull existing rows for this period so we can update in place
    // (UPSERT-by-(item, period) without a unique constraint — the
    // catalog is small and one import is at most a few dozen rows).
    const { data: existingForPeriod } = await supabase
      .from('item_sales')
      .select('id, item_id, item_name, period_start, period_end')
      .eq('restaurant_id', restId)
      .eq('period_start', period_start)
      .eq('period_end',   period_end)

    // Lookup by composite key. Matched rows key on item_id; unmatched
    // rows key on lower-cased item_name (so they still upsert-overwrite
    // on a re-import even before the manager creates the item).
    const existingByKey = new Map()
    for (const e of existingForPeriod ?? []) {
      const key = e.item_id
        ? `id:${e.item_id}`
        : `name:${(e.item_name || '').toLowerCase().trim()}`
      existingByKey.set(key, e)
    }

    let inserted = 0, updated = 0, unmatched = 0
    for (const r of rows) {
      const matched =
        (r.toast_guid && byGuid.get(r.toast_guid)) ||
        byName.get((r.item_name || '').toLowerCase().trim()) ||
        null
      if (!matched) unmatched++

      const key = matched
        ? `id:${matched.id}`
        : `name:${(r.item_name || '').toLowerCase().trim()}`
      const existing = existingByKey.get(key)

      const payload = {
        item_id:       matched?.id ?? null,
        item_name:     r.item_name,
        period_start,
        period_end,
        quantity_sold: Number(r.quantity_sold || 0),
        gross_sales:   r.gross_sales != null ? Number(r.gross_sales) : null,
        net_sales:     r.net_sales   != null ? Number(r.net_sales)   : null,
        source:        'csv',
      }

      if (existing) {
        const { error: updErr } = await supabase
          .from('item_sales')
          .update({ ...payload, imported_at: new Date().toISOString() })
          .eq('id', existing.id)
        if (updErr) throw updErr
        updated++
      } else {
        const { error: insErr } = await supabase
          .from('item_sales')
          .insert({ restaurant_id: restId, ...payload })
        if (insErr) throw insErr
        inserted++
      }
    }

    await fetchAll()
    return { inserted, updated, unmatched }
  }, [restaurant, items, fetchAll])

  // ---------- Set inventory baseline ------------------------------
  // Snapshots every active item's current_on_hand into on_hand_snapshots
  // with source='baseline'. The variance report uses this as the
  // start-of-period inventory level. The manager hits this button
  // after their first stocktake of the period (typically Sunday night
  // for a Mon–Sun reporting week).
  //
  // Returns: { snapshot_count, snapshot_at }
  const setInventoryBaseline = useCallback(async () => {
    if (!restaurant) throw new Error('No restaurant loaded')
    const restId = restaurant.id
    const active = (items || []).filter((i) => i.active)
    if (active.length === 0) {
      throw new Error('No active items to snapshot')
    }
    const snapshot_at = new Date().toISOString()
    const inserts = active.map((i) => ({
      restaurant_id: restId,
      item_id:       i.id,
      snapshot_at,
      quantity:      Number(i.current_on_hand || 0),
      source:        'baseline',
      notes:         'Set via "Set inventory baseline" button',
    }))
    const { error: insErr } = await supabase
      .from('on_hand_snapshots')
      .insert(inserts)
    if (insErr) throw insErr
    await fetchAll()
    return { snapshot_count: inserts.length, snapshot_at }
  }, [restaurant, items, fetchAll])

  return {
    restaurant,
    items,
    activeItems,
    inactiveItems,
    suppliers,
    unmatchedSuppliers,
    staff,
    salesSummary,
    loading,
    error,
    refresh: fetchAll,
    updateRestaurant,
    updateItem,
    createItem,
    importItemsFromCsv,
    updateSupplier,
    createSupplier,
    promoteSupplier,
    updateStaff,
    createStaff,
    importStaffFromCsv,
    importDailySales,
    deleteDailySales,
    deleteAllDailySales,
    itemSalesSummary,
    importItemSales,
    latestBaseline,
    setInventoryBaseline,
  }
}
